/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { Ellipsis, Icon } from '@zoom/components';
import { addDays } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'reactstrap';

import { useAuth } from '../../../../app/AuthorizationProvider';
import { suppressContextMenuHandler } from '../../../../components/Dropdown/UncontrolledDropdown';
import { TimezoneName } from '../../../../components/TimezoneName';
import { Queue, Skill } from '../../../../graphql/components';
import { getUTCHoursOffsetLabel } from '../../../../utils/date';
import { fullNameOfAgent } from '../../../../utils/helpers';
import { Agent } from '../../../Agents/types';
import { getHoursPerAgentButtonTag, getScheduledHoursTag } from '../../constants';

interface HoursPerAgentProps {
  agent: Agent;
  date: Date;
  queues: Record<string, Queue>;
  skills: Record<string, Skill>;
  durationData: {
    workDuration: number;
    totalDuration: number;
    index: number;
  };
  scrollbarContainerRef: HTMLElement | null;
}

export const ScheduledHoursPerAgent: React.FC<HoursPerAgentProps> = ({
  agent,
  date,
  queues,
  skills,
  durationData: { index, totalDuration, workDuration },
  scrollbarContainerRef,
}) => {
  const { t } = useTranslation();
  const { locale } = useAuth();

  const [menuOpened, setMenuOpened] = useState(false);
  const [isContainerAttached, setContainerAttached] = useState(false);

  const iconRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const workHours: number = Math.floor(workDuration / 60);
  const workMinutes: number = workDuration % 60;
  const totalHours: number = Math.floor(totalDuration / 60);
  const totalMinutes: number = totalDuration % 60;

  useEffect(() => {
    setContainerAttached(true);
  }, []);

  const hideMenu = () => {
    setMenuOpened(false);
    document.removeEventListener('mousedown', handleMenuDismiss);
    if (scrollbarContainerRef) {
      scrollbarContainerRef.removeEventListener('scroll', handleMenuDismiss);
    }
  };

  const handleMenuDismiss = (e: Event) => {
    if (contextMenuRef && contextMenuRef.current && e.target && !contextMenuRef.current.contains(e.target as Node)) {
      hideMenu();
    }
  };

  const menuHandler = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    setMenuOpened(true);
    document.addEventListener('mousedown', handleMenuDismiss);
    if (scrollbarContainerRef) {
      scrollbarContainerRef.addEventListener('scroll', handleMenuDismiss);
    }
    return false;
  };

  return (
    <>
      <div ref={iconRef} title={t('schedule.hoursPerPerson')}>
        <Icon
          onClick={menuHandler}
          kind="ico_question-mark"
          size={14}
          data-test-id={getHoursPerAgentButtonTag(index)}
          color="blue-light"
        />
      </div>
      {isContainerAttached && (
        <Popover
          trigger="click"
          isOpen={menuOpened}
          target={iconRef.current!}
          placement="top"
          fade={false}
          boundariesElement="window"
          className="hours-per-agent__scheduled-popover"
          modifiers={{ offset: { enabled: true, offset: '105, 0' } }}
        >
          <div ref={contextMenuRef} onContextMenu={suppressContextMenuHandler}>
            <div className="popover-menu__header">
              {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
              <Ellipsis>{fullNameOfAgent(agent)}</Ellipsis>
            </div>
            <div className="popover-menu__body">
              <div className="popover-menu__queues-wrapper">
                <div className="popover-menu__queues-heading">
                  <div className="popover-menu__queues-title">{t('queue.queues')}</div>
                  <div className="popover-menu__skills-title">{t('skill.skills')}</div>
                </div>
                {/* eslint-disable-next-line array-callback-return */}
                {agent.queueIds?.map((queueId) => {
                  const queue = queues[queueId];

                  // Render only queue info related to queues selected to Schedule
                  if (queue) {
                    const skill = queue.skillId ? skills[queue.skillId] : undefined;
                    return (
                      <div className="popover-menu__queue-wrapper" key={queueId}>
                        <div className="popover-menu__queue-name">{queue.name}</div>
                        {skill && <div className="popover-menu__queue-skill">{skill.name}</div>}
                      </div>
                    );
                  }
                })}
              </div>
              <div className="popover-menu__body-heading">{t('schedule.workingTotalSchedules')}</div>
              <div className="popover-menu__block-wrapper">
                <div>{t('schedule.workScheduled')}</div>
                <div data-test-id={getScheduledHoursTag(index, 'work')} className="work-hours-label">
                  <strong>{workHours}</strong>
                  {` ${t('time.hrs')} `}
                  <strong>{workMinutes}</strong>
                  {` ${t('time.min')}`}
                </div>
              </div>
              <div className="popover-menu__block-wrapper">
                <div>{t('schedule.totalScheduled')}</div>
                <div data-test-id={getScheduledHoursTag(index, 'total')} className="total-hours-label">
                  <strong>{totalHours}</strong>
                  {` ${t('time.hrs')} `}
                  <strong>{totalMinutes}</strong>
                  {` ${t('time.min')}`}
                </div>
              </div>
              <div className="popover-menu__body-heading">{t('timezone.timezone', { count: 1 })}</div>
              <div className="popover-menu__block-wrapper">
                <TimezoneName timezone={agent.timezone} />
                <div data-test-id="timezone-offset">
                  {getUTCHoursOffsetLabel(addDays(date, 1), agent.timezone, locale, t)}
                </div>
              </div>
            </div>
          </div>
        </Popover>
      )}
    </>
  );
};
